import { AxiosError } from 'axios';
import { applicantApi } from './api';

// Document validation types
export interface DocumentValidationRequest {
  base64_pdf: string;
  llm_model: string;
  upload_precheck: boolean;
}

export interface DocumentValidationResponse {
  data: {
    detected_at: string;
    is_scanned: boolean;
    is_print_to_pdf: boolean;
    doc_type: string;
    blacklisted_source: any;
  };
  status: 'success' | 'error';
  message: string;
}

// Convert file to base64
export const fileToBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      // Remove data:application/pdf;base64, prefix
      const base64 = (reader.result as string).split(',')[1];
      resolve(base64);
    };
    reader.onerror = error => reject(error);
  });
};

// Validate document using internal API (which proxies to Lambda)
export const validateDocumentWithLambda = async (base64Pdf: string): Promise<DocumentValidationResponse> => {
  try {
    const response = await applicantApi.post('/application/validate-document', {
      base64_pdf: base64Pdf,
    });

    return response.data; // API response wraps data in { status, message, data }
  } catch (err) {
    console.log('Document validation failed', (err as AxiosError).message);
    throw new Error('Unable to validate document. Please try again.');
  }
};

// Validate a single file
export const validateSingleDocument = async (file: File): Promise<{ isValid: boolean; errorMessage?: string }> => {
  try {
    // Convert file to base64
    const base64 = await fileToBase64(file);

    // Validate with Lambda
    const validationResp = await validateDocumentWithLambda(base64);
    const validation = validationResp.data;

    // Check validation results
    if (validation.is_scanned) {
      return {
        isValid: false,
        errorMessage:
          'This document looks like a scan or photo of a printed page, which might impact your application. Please upload the original digital PDF issued by your employer or financial institution — not a scanned, photographed, or photocopied version.',
      };
    }

    if (validation.is_print_to_pdf) {
      return {
        isValid: false,
        errorMessage:
          'This file looks like it was re-saved or “printed to PDF” from a browser or PDF viewer, which might impact your application. Please log in to your employer or bank portal and download the original PDF directly — avoid using “Print”, “Save as PDF”, or screenshot tools.',
      };
    }

    if (validation.doc_type === 'Other') {
      return {
        isValid: false,
        errorMessage:
          'We couldn’t identify the type of document you uploaded. Please make sure you’re uploading a recent paystub, bank statement, or offer letter that clearly shows your name and the institution’s name.',
      };
    }

    if (validation.blacklisted_source && validation.blacklisted_source.name) {
      return {
        isValid: false,
        errorMessage: `The document you uploaded appears to have been generated by a known document generator tool (${validation.blacklisted_source.name}), which might impact your application. Please log in to your employer or bank portal and download the original PDF directly — avoid using “Print”, “Save as PDF”, or screenshot tools.`,
      };
    }

    if (validationResp.status === 'error') {
      return {
        isValid: false,
        errorMessage: validationResp.message || 'Document validation failed. Please try again.',
      };
    }

    // Document is valid
    return { isValid: true };
  } catch (error) {
    return {
      isValid: false,
      errorMessage: error instanceof Error ? error.message : 'Document validation failed. Please try again.',
    };
  }
};
